import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
  Check,
  Copy,
  Clock,
  ShieldCheck,
  ArrowLeft,
  RefreshCw,
  AlertTriangle,
  Wallet,
  Send,
  Sparkles,
  CheckCircle2,
  Loader2,
} from "lucide-react";

import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../lib/api";
import { copyToClipboard } from "../../lib/clipboard";
import { ProfileShell } from "../../components/profile/ProfileShell";

const POLL_MS = 5000;

// Map NowPayments pay_currency code → brand logo + colour
const CRYPTO_BRAND = {
  usdttrc20: { logo: "/payment-logos/usdt.svg", bg: "#26a17b", name: "USDT", network: "TRC-20", netColor: "#ef0027" },
  usdtbsc:   { logo: "/payment-logos/usdt.svg", bg: "#26a17b", name: "USDT", network: "BEP-20", netColor: "#f3ba2f", netTextColor: "#1a1208" },
  usdtbep20: { logo: "/payment-logos/usdt.svg", bg: "#26a17b", name: "USDT", network: "BEP-20", netColor: "#f3ba2f", netTextColor: "#1a1208" },
  usdterc20: { logo: "/payment-logos/usdt.svg", bg: "#26a17b", name: "USDT", network: "ERC-20", netColor: "#627eea" },
  usdcmatic: { logo: "/payment-logos/usdc.svg", bg: "#2775ca", name: "USDC", network: "Polygon", netColor: "#8247e5" },
  usdc:      { logo: "/payment-logos/usdc.svg", bg: "#2775ca", name: "USDC", network: "Polygon", netColor: "#8247e5" },
  trx:       { logo: "/payment-logos/trx.svg",  bg: "#ef0027", name: "TRX",  network: "TRC-20", netColor: "#ffffff", netTextColor: "#ef0027" },
  bnbbsc:    { logo: "/payment-logos/bnb.svg",  bg: "#f3ba2f", name: "BNB",  network: "BEP-20", netColor: "#212121" },
  bnb:       { logo: "/payment-logos/bnb.svg",  bg: "#f3ba2f", name: "BNB",  network: "BEP-20", netColor: "#212121" },
  eth:       { logo: "/payment-logos/eth.svg",  bg: "#627eea", name: "ETH",  network: "ERC-20", netColor: "#3c3c3d" },
  ton:       { logo: "/payment-logos/ton.svg",  bg: "#0098ea", name: "TON",  network: "TON",    netColor: "#0098ea" },
  btc:       { logo: "/payment-logos/btc.svg",  bg: "#f7931a", name: "BTC",  network: "BTC",    netColor: "#f7931a" },
};

function getBrand(code = "") {
  const k = String(code).toLowerCase().replace(/[^a-z0-9]/g, "");
  return CRYPTO_BRAND[k] || null;
}

const STATUS_PRETTY = {
  waiting: { label: "Waiting for transfer", color: "amber", step: 0, description: "Send the exact crypto amount from your wallet" },
  confirming: { label: "Detected on-chain", color: "amber", step: 1, description: "Awaiting blockchain confirmations" },
  confirmed: { label: "Confirmed", color: "emerald", step: 2, description: "Crediting your balance now" },
  sending: { label: "Crediting balance", color: "emerald", step: 2, description: "Almost done — refreshing your wallet" },
  finished: { label: "Completed", color: "emerald", step: 3, description: "Funds successfully added to your balance" },
  failed: { label: "Payment failed", color: "rose", step: -1, description: "The transaction did not complete" },
  expired: { label: "Address expired", color: "rose", step: -1, description: "Please create a new deposit" },
  partially_paid: { label: "Partial payment received", color: "amber", step: 1, description: "Send the remaining amount to complete" },
};

function CopyField({ label, value, mono = true, testid, accent = "#26a17b" }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    const ok = await copyToClipboard(value || "");
    if (ok) {
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1800);
    }
  };
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[10.5px] uppercase tracking-[0.22em] text-white/40 font-bold">
          {label}
        </span>
        {copied && (
          <span className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold flex items-center gap-1 animate-in fade-in duration-200">
            <Check className="h-3 w-3" /> Copied
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={handleCopy}
        data-testid={testid}
        className={`group w-full rounded-xl px-4 py-3 flex items-center gap-3 bg-white/[0.025] hover:bg-white/[0.05] border border-white/10 hover:border-white/20 text-left transition-all ${
          mono ? "font-mono" : ""
        }`}
      >
        <span className="flex-1 min-w-0 text-white text-[13.5px] break-all leading-snug">
          {value || "—"}
        </span>
        <span
          className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0 transition-all"
          style={{
            background: copied ? `${accent}26` : "rgba(255,255,255,0.04)",
            border: `1px solid ${copied ? `${accent}66` : "rgba(255,255,255,0.10)"}`,
          }}
        >
          {copied ? (
            <Check className="h-4 w-4" style={{ color: accent }} />
          ) : (
            <Copy className="h-4 w-4 text-white/50 group-hover:text-white/85 transition" />
          )}
        </span>
      </button>
    </div>
  );
}

function useCountdown(targetIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!targetIso) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [targetIso]);
  if (!targetIso) return null;
  const target = Date.parse(targetIso);
  if (!Number.isFinite(target)) return null;
  const diff = Math.max(0, target - now);
  const m = Math.floor(diff / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { minutes: m, seconds: s, expired: diff <= 0, raw: diff };
}

function StepDot({ active, done, label, Icon }) {
  const tone = done ? "emerald" : active ? "amber" : "muted";
  const ring =
    tone === "emerald"
      ? "bg-emerald-500/15 border-emerald-400/55 text-emerald-300"
      : tone === "amber"
      ? "bg-amber-500/15 border-amber-400/55 text-amber-300"
      : "bg-white/[0.025] border-white/10 text-white/35";
  return (
    <div className="flex flex-col items-center gap-1.5 flex-1 min-w-0">
      <div
        className={`relative h-9 w-9 rounded-xl border flex items-center justify-center transition-all ${ring}`}
      >
        {active && !done && (
          <span className="absolute inset-0 rounded-xl animate-ping opacity-50 bg-amber-400/30" />
        )}
        <Icon className="h-4 w-4 relative" strokeWidth={2.2} />
      </div>
      <span
        className={`text-[10px] font-semibold uppercase tracking-wider truncate max-w-full ${
          done ? "text-emerald-300/85" : active ? "text-amber-200" : "text-white/40"
        }`}
      >
        {label}
      </span>
    </div>
  );
}

function StepBar({ stepIndex }) {
  const steps = [
    { key: 0, label: "Send", icon: Send },
    { key: 1, label: "Confirm", icon: Loader2 },
    { key: 2, label: "Credit", icon: Sparkles },
    { key: 3, label: "Done", icon: CheckCircle2 },
  ];
  return (
    <div className="flex items-center gap-1.5 px-1">
      {steps.map((s, i) => (
        <div key={s.key} className="flex items-center gap-1.5 flex-1">
          <StepDot
            active={i === stepIndex}
            done={i < stepIndex || stepIndex === 3}
            label={s.label}
            Icon={s.icon}
          />
          {i < steps.length - 1 && (
            <div
              className={`h-px flex-1 mb-5 transition-colors ${
                i < stepIndex || stepIndex === 3 ? "bg-emerald-400/45" : "bg-white/10"
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );
}

export default function CryptoCashierPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const poll = async () => {
      try {
        const { data } = await api.get(`/profile/deposits/${id}/crypto`);
        if (cancelled) return;
        setInfo(data);
        setError("");
        const ps = (data?.payment_status || "").toLowerCase();
        const st = (data?.status || "").toLowerCase();
        if (
          ["finished", "failed", "expired", "refunded"].includes(ps) ||
          ["completed", "failed"].includes(st)
        ) {
          if (st === "completed" || data?.credited) {
            await refreshUser();
            toast.success(`Deposit credited — ৳${(data?.amount_bdt || 0).toLocaleString()} added to your balance`);
            setTimeout(() => {
              if (!cancelled) navigate("/profile/deposit-history?from=crypto");
            }, 2500);
          } else if (ps === "failed" || ps === "expired" || ps === "refunded") {
            toast.error(`Payment ${ps} — please try again or contact support`);
          }
          return;
        }
        timer = setTimeout(poll, POLL_MS);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Failed to load deposit");
        timer = setTimeout(poll, POLL_MS * 2);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [id, tick, refreshUser, navigate]);

  const qrValue = useMemo(() => {
    return (info?.pay_address || "").trim();
  }, [info?.pay_address]);

  const ps = (info?.payment_status || "").toLowerCase();
  const stKey = info?.status === "completed" ? "finished" : ps;
  const stMeta = STATUS_PRETTY[stKey] || { label: info?.payment_status || "Pending", color: "amber", step: 0, description: "" };

  const countdown = useCountdown(info?.expiration_estimate_date);
  const brand = info ? getBrand(info.pay_currency) : null;
  const accent = brand?.bg || "#26a17b";

  const statusToneClasses =
    stMeta.color === "emerald"
      ? "bg-emerald-500/8 border-emerald-400/30 text-emerald-200"
      : stMeta.color === "rose"
      ? "bg-rose-500/8 border-rose-400/30 text-rose-200"
      : "bg-amber-500/8 border-amber-400/30 text-amber-200";

  const dotToneClasses =
    stMeta.color === "emerald" ? "bg-emerald-400" : stMeta.color === "rose" ? "bg-rose-400" : "bg-amber-400";

  return (
    <ProfileShell title="Crypto Deposit">
      <div className="max-w-xl mx-auto pb-32 lg:pb-10 relative">
        {/* Decorative ambient glow */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-20 left-1/2 -translate-x-1/2 h-72 w-[420px] rounded-full opacity-[0.18] blur-3xl"
          style={{ background: `radial-gradient(closest-side, ${accent} 0%, transparent 75%)` }}
        />

        <button
          type="button"
          data-testid="crypto-back-btn"
          onClick={() => navigate("/profile/deposit")}
          className="relative inline-flex items-center gap-1.5 text-sm text-white/55 hover:text-white mb-5 group"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
          <span>Back to deposit</span>
        </button>

        {loading && !info ? (
          <div className="rounded-2xl p-10 text-center text-white/55 border border-white/10 bg-[#0c1320]">
            <Loader2 className="h-5 w-5 mx-auto mb-3 animate-spin text-white/40" />
            Loading payment details…
          </div>
        ) : error && !info ? (
          <div className="rounded-2xl p-6 text-rose-200 border border-rose-500/30 bg-rose-500/10 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" />
            <div>
              <div className="font-bold">Could not load deposit</div>
              <div className="text-[13px] opacity-85 mt-1">{error}</div>
            </div>
          </div>
        ) : info ? (
          <div className="space-y-4 relative animate-in fade-in slide-in-from-bottom-2 duration-500" data-testid="crypto-cashier">
            {/* === HERO: brand + amount + status === */}
            <div
              className="relative rounded-3xl p-5 sm:p-6 overflow-hidden"
              style={{
                background: `radial-gradient(140% 100% at 100% 0%, ${accent}1f 0%, rgba(8,14,22,0) 65%), linear-gradient(180deg,#0e1a26 0%,#0a1119 100%)`,
                border: `1px solid ${accent}33`,
                boxShadow: `0 28px 80px -38px ${accent}aa, inset 0 1px 0 rgba(255,255,255,0.04)`,
              }}
            >
              {/* subtle dot grid */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-[0.05]"
                style={{
                  backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
                  backgroundSize: "16px 16px",
                }}
              />

              <div className="relative flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-[10.5px] uppercase tracking-[0.26em] text-white/45 font-bold">
                    Deposit · You will receive
                  </div>
                  <div className="flex items-baseline gap-2 mt-2">
                    <span className="text-[12px] text-white/45">৳</span>
                    <span
                      className="text-[40px] sm:text-[44px] leading-[0.95] font-extrabold text-white tabular-nums"
                      style={{ letterSpacing: "-0.025em" }}
                    >
                      {Number(info.amount_bdt || 0).toLocaleString("en-IN")}
                    </span>
                  </div>
                  <div className="mt-1.5 text-[12.5px] text-white/55 font-medium">
                    ≈ ${Number(info.price_amount || 0).toFixed(2)} {info.price_currency || "USD"} ·{" "}
                    via {brand ? `${brand.name} · ${brand.network}` : info.method_label || info.method}
                  </div>
                </div>

                {brand && (
                  <div
                    className="relative h-16 w-16 rounded-2xl flex items-center justify-center shrink-0 overflow-hidden"
                    style={{
                      background: `linear-gradient(135deg, ${brand.bg} 0%, ${brand.bg}cc 100%)`,
                      boxShadow: `0 16px 36px -14px ${brand.bg}cc, inset 0 1px 0 rgba(255,255,255,0.22)`,
                      padding: 11,
                    }}
                    data-testid="crypto-amount-logo"
                  >
                    <img src={brand.logo} alt={brand.name} className="h-full w-full object-contain drop-shadow" draggable={false} />
                    <span
                      className="absolute -bottom-[1px] -right-[1px] px-[5px] py-[2px] rounded-tl-lg text-[8px] font-extrabold uppercase leading-[9px] tracking-wider"
                      style={{ background: brand.netColor, color: brand.netTextColor || "#fff" }}
                    >
                      {brand.network.replace("-", "")}
                    </span>
                  </div>
                )}
              </div>

              {/* Status pill row */}
              <div className={`relative mt-5 rounded-2xl px-4 py-3 border ${statusToneClasses}`}>
                <div className="flex items-center gap-2.5">
                  <span className="relative flex h-2.5 w-2.5">
                    {stMeta.color !== "rose" && (
                      <span className={`absolute inline-flex h-full w-full rounded-full ${dotToneClasses} opacity-60 animate-ping`} />
                    )}
                    <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${dotToneClasses}`} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13.5px] font-bold leading-tight">{stMeta.label}</div>
                    {stMeta.description && (
                      <div className="text-[11.5px] opacity-70 mt-0.5 leading-snug">{stMeta.description}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    data-testid="crypto-refresh-btn"
                    onClick={() => setTick((x) => x + 1)}
                    className="h-8 w-8 rounded-lg hover:bg-white/5 inline-flex items-center justify-center transition shrink-0"
                    aria-label="Refresh status"
                    title="Refresh"
                  >
                    <RefreshCw className="h-3.5 w-3.5 opacity-75" />
                  </button>
                </div>

                {info.actually_paid > 0 && (
                  <div className="mt-2 pt-2 border-t border-white/10 text-[11.5px] opacity-85">
                    Received so far:{" "}
                    <span className="font-mono font-bold">{info.actually_paid}</span>{" "}
                    {info.pay_currency?.toUpperCase()}
                  </div>
                )}
              </div>

              {/* Step indicator */}
              {stMeta.step >= 0 && (
                <div className="relative mt-5">
                  <StepBar stepIndex={Math.min(stMeta.step, 3)} />
                </div>
              )}

              {/* Countdown */}
              {countdown && !countdown.expired && stMeta.step < 2 && (
                <div className="relative mt-4 flex items-center justify-center gap-1.5 text-[11.5px] text-white/55">
                  <Clock className="h-3.5 w-3.5" />
                  <span>
                    Address expires in{" "}
                    <span className="font-mono font-bold text-white/85 tabular-nums">
                      {String(countdown.minutes).padStart(2, "0")}:
                      {String(countdown.seconds).padStart(2, "0")}
                    </span>
                  </span>
                </div>
              )}
            </div>

            {/* === QR + payment details === */}
            <div
              className="relative rounded-3xl overflow-hidden"
              style={{
                background: "linear-gradient(180deg,#0e1a26 0%,#0a1119 100%)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              {/* QR section */}
              {qrValue && (
                <div className="px-5 pt-6 pb-5 flex flex-col items-center">
                  <div className="text-[10.5px] uppercase tracking-[0.24em] text-white/40 font-bold mb-3 flex items-center gap-1.5">
                    <Wallet className="h-3 w-3" /> Scan or paste address
                  </div>
                  <div className="relative">
                    {/* corner brackets */}
                    {["top-0 left-0 border-t-2 border-l-2", "top-0 right-0 border-t-2 border-r-2", "bottom-0 left-0 border-b-2 border-l-2", "bottom-0 right-0 border-b-2 border-r-2"].map((pos, i) => (
                      <span
                        key={i}
                        className={`absolute ${pos} h-5 w-5 rounded-[3px] pointer-events-none`}
                        style={{ borderColor: `${accent}80` }}
                      />
                    ))}
                    <div
                      className="rounded-2xl p-3"
                      style={{
                        background: "white",
                        boxShadow: `0 28px 60px -28px ${accent}66, 0 0 0 1px ${accent}25`,
                      }}
                    >
                      <div
                        className="block rounded-md overflow-hidden bg-white"
                        data-testid="crypto-qr-image"
                        style={{ width: 240, height: 240 }}
                      >
                        <QRCodeSVG
                          value={qrValue}
                          size={240}
                          bgColor="#ffffff"
                          fgColor="#0a1119"
                          level="H"
                          includeMargin={false}
                          imageSettings={
                            brand
                              ? {
                                  src: brand.logo,
                                  height: 44,
                                  width: 44,
                                  excavate: true,
                                }
                              : undefined
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* divider */}
              <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

              {/* Copy fields */}
              <div className="p-5 space-y-4">
                <CopyField
                  label="Send exactly"
                  value={info.pay_amount ? `${info.pay_amount} ${info.pay_currency?.toUpperCase()}` : ""}
                  mono
                  testid="crypto-pay-amount"
                  accent={accent}
                />
                <CopyField
                  label="To address"
                  value={info.pay_address}
                  mono
                  testid="crypto-pay-address"
                  accent={accent}
                />

                {/* Warning */}
                <div
                  className="rounded-xl px-3.5 py-3 flex items-start gap-2.5 text-[12px] leading-relaxed"
                  style={{
                    background: "linear-gradient(135deg, rgba(245,158,11,0.10) 0%, rgba(245,158,11,0.04) 100%)",
                    border: "1px solid rgba(245,158,11,0.30)",
                    color: "#fcd34d",
                  }}
                >
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    Send the <strong className="text-amber-200">exact</strong> amount on the{" "}
                    <strong className="text-amber-200">
                      {brand ? `${brand.name} ${brand.network}` : info.pay_currency?.toUpperCase()}
                    </strong>{" "}
                    network only. Wrong network or wrong amount may result in lost funds. This address is single-use.
                  </span>
                </div>
              </div>
            </div>

            {/* === Footer info === */}
            <div className="grid grid-cols-2 gap-2.5">
              <div className="rounded-xl px-3.5 py-2.5 bg-white/[0.025] border border-white/10 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-emerald-400 shrink-0" />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-white/40 font-bold">Auto-credit</div>
                  <div className="text-[11.5px] text-white/75 font-medium truncate">On confirmation</div>
                </div>
              </div>
              <div className="rounded-xl px-3.5 py-2.5 bg-white/[0.025] border border-white/10 flex items-center gap-2">
                <RefreshCw className="h-4 w-4 text-sky-400 shrink-0" />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-white/40 font-bold">Live polling</div>
                  <div className="text-[11.5px] text-white/75 font-medium truncate">Every 5 seconds</div>
                </div>
              </div>
            </div>

            {/* Payment ID footer */}
            {info.payment_id && (
              <div className="text-[10.5px] text-white/30 text-center font-mono pt-1 select-all break-all">
                Ref · {info.payment_id}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </ProfileShell>
  );
}
